// =============================================================================
// Lumen Viewer — minimal DICOM parser (browser, ArrayBuffer in)
// =============================================================================
// Parses enough for worklist render + windowing + metadata panel:
//   - File meta group 0002 (to get TransferSyntaxUID)
//   - Patient / study / series safe descriptors for the metadata panel
//   - Pixel-rendering tags (Rows/Cols/BitsAllocated/…, WC/WW, Rescale)
//   - PixelData slice
//
// Only implicit/explicit VR little-endian transfer syntaxes are supported.
// Throws on compressed / big-endian / encapsulated PixelData.
// =============================================================================

const PREAMBLE_LEN = 128;
const TS_IMPLICIT_LE = '1.2.840.10008.1.2';
const TS_EXPLICIT_LE = '1.2.840.10008.1.2.1';

const LONG_FORM_VRS = new Set(['OB','OW','OD','OF','OL','OV','SQ','UC','UN','UR','UT']);

// Displayable (non-PHI from viewer POV — still shown in metadata panel,
// but patient fields are only rendered if the caller explicitly opts in).
const READ_TAGS = {
  // File meta
  0x00020010: { key: 'TransferSyntaxUID', vr: 'UI' },
  // Study / series descriptors
  0x00080016: { key: 'SOPClassUID', vr: 'UI' },
  0x00080018: { key: 'SOPInstanceUID', vr: 'UI' },
  0x00080020: { key: 'StudyDate', vr: 'DA' },
  0x00080030: { key: 'StudyTime', vr: 'TM' },
  0x00080060: { key: 'Modality', vr: 'CS' },
  0x00081030: { key: 'StudyDescription', vr: 'LO' },
  0x0008103E: { key: 'SeriesDescription', vr: 'LO' },
  0x00180015: { key: 'BodyPartExamined', vr: 'CS' },
  0x0020000D: { key: 'StudyInstanceUID', vr: 'UI' },
  0x0020000E: { key: 'SeriesInstanceUID', vr: 'UI' },
  0x00200013: { key: 'InstanceNumber', vr: 'IS' },
  // Pixel-render tags
  0x00280002: { key: 'SamplesPerPixel', vr: 'US' },
  0x00280004: { key: 'PhotometricInterpretation', vr: 'CS' },
  0x00280010: { key: 'Rows', vr: 'US' },
  0x00280011: { key: 'Columns', vr: 'US' },
  0x00280030: { key: 'PixelSpacing', vr: 'DS' },
  0x00280100: { key: 'BitsAllocated', vr: 'US' },
  0x00280101: { key: 'BitsStored', vr: 'US' },
  0x00280102: { key: 'HighBit', vr: 'US' },
  0x00280103: { key: 'PixelRepresentation', vr: 'US' },
  0x00281050: { key: 'WindowCenter', vr: 'DS' },
  0x00281051: { key: 'WindowWidth', vr: 'DS' },
  0x00281052: { key: 'RescaleIntercept', vr: 'DS' },
  0x00281053: { key: 'RescaleSlope', vr: 'DS' },
  0x7FE00010: { key: 'PixelData', vr: 'OB' },
};

function tagKey(group, element) { return ((group << 16) >>> 0) | element; }

function readTagHeader(view, offset, explicitVR) {
  if (offset + 8 > view.byteLength) return null;
  const group   = view.getUint16(offset, true);
  const element = view.getUint16(offset + 2, true);

  if (explicitVR) {
    const vr = String.fromCharCode(view.getUint8(offset + 4), view.getUint8(offset + 5));
    if (LONG_FORM_VRS.has(vr)) {
      if (offset + 12 > view.byteLength) return null;
      const length = view.getUint32(offset + 8, true);
      return {
        group, element, vr,
        valueOffset: offset + 12,
        length,
        nextOffset: length === 0xFFFFFFFF ? offset + 12 : offset + 12 + length,
        undefinedLength: length === 0xFFFFFFFF,
      };
    }
    const length = view.getUint16(offset + 6, true);
    return { group, element, vr, valueOffset: offset + 8, length, nextOffset: offset + 8 + length, undefinedLength: false };
  }
  // implicit
  const length = view.getUint32(offset + 4, true);
  return {
    group, element, vr: null,
    valueOffset: offset + 8,
    length,
    nextOffset: length === 0xFFFFFFFF ? offset + 8 : offset + 8 + length,
    undefinedLength: length === 0xFFFFFFFF,
  };
}

function readString(view, t) {
  const bytes = new Uint8Array(view.buffer, view.byteOffset + t.valueOffset, t.length);
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return s.replace(/\0+$/, '').trim();
}

function readDS(view, t) {
  const raw = readString(view, t);
  if (!raw) return NaN;
  const first = raw.split('\\')[0];
  const n = Number(first);
  return Number.isFinite(n) ? n : NaN;
}

function readUS(view, t) { return view.getUint16(t.valueOffset, true); }

function skipUndefinedLengthSequence(view, start) {
  let off = start;
  while (off + 8 <= view.byteLength) {
    const g = view.getUint16(off, true);
    const e = view.getUint16(off + 2, true);
    const len = view.getUint32(off + 4, true);
    off += 8;
    if (g === 0xFFFE && e === 0xE0DD) return off;
    if (g === 0xFFFE && e === 0xE000) {
      if (len === 0xFFFFFFFF) off = skipUndefinedLengthSequence(view, off);
      else off += len;
    } else {
      throw new Error('dicom-parser: malformed sequence');
    }
  }
  throw new Error('dicom-parser: truncated sequence');
}

export function parseDICOM(arrayBuffer) {
  const view = new DataView(arrayBuffer);
  if (view.byteLength < PREAMBLE_LEN + 4) throw new Error('dicom-parser: too small');
  const magic = String.fromCharCode(
    view.getUint8(PREAMBLE_LEN),
    view.getUint8(PREAMBLE_LEN + 1),
    view.getUint8(PREAMBLE_LEN + 2),
    view.getUint8(PREAMBLE_LEN + 3)
  );
  if (magic !== 'DICM') throw new Error('dicom-parser: missing DICM magic');

  // ---- File Meta (always Explicit VR LE) --------------------------------
  let off = PREAMBLE_LEN + 4;
  let transferSyntax = null;
  let metaEnd = null;
  {
    const t = readTagHeader(view, off, true);
    if (!t || t.group !== 0x0002 || t.element !== 0x0000) {
      throw new Error('dicom-parser: missing FileMetaInfoGroupLength');
    }
    const groupLen = view.getUint32(t.valueOffset, true);
    metaEnd = t.nextOffset + groupLen;
    off = t.nextOffset;
  }
  while (off < metaEnd) {
    const t = readTagHeader(view, off, true);
    if (!t || t.group !== 0x0002) break;
    if (t.element === 0x0010) transferSyntax = readString(view, t);
    off = t.nextOffset;
  }
  if (!transferSyntax) throw new Error('dicom-parser: TransferSyntaxUID not found');
  if (transferSyntax !== TS_IMPLICIT_LE && transferSyntax !== TS_EXPLICIT_LE) {
    throw new Error(`dicom-parser: unsupported transfer syntax ${transferSyntax}`);
  }
  const explicitVR = transferSyntax === TS_EXPLICIT_LE;

  // ---- Dataset ----------------------------------------------------------
  const out = {
    transferSyntax,
    // sensible defaults
    samplesPerPixel: 1,
    photometricInterpretation: 'MONOCHROME2',
    pixelRepresentation: 0,
    rescaleSlope: 1,
    rescaleIntercept: 0,
  };

  off = metaEnd;
  while (off + 8 <= view.byteLength) {
    const t = readTagHeader(view, off, explicitVR);
    if (!t) break;

    const k = tagKey(t.group, t.element);
    const spec = READ_TAGS[k];

    if (!spec) {
      off = t.undefinedLength ? skipUndefinedLengthSequence(view, t.valueOffset) : t.nextOffset;
      continue;
    }

    switch (k) {
      case 0x00080016: out.sopClassUID              = readString(view, t); break;
      case 0x00080018: out.sopInstanceUID           = readString(view, t); break;
      case 0x00080020: out.studyDate                = readString(view, t); break;
      case 0x00080030: out.studyTime                = readString(view, t); break;
      case 0x00080060: out.modality                 = readString(view, t); break;
      case 0x00081030: out.studyDescription         = readString(view, t); break;
      case 0x0008103E: out.seriesDescription        = readString(view, t); break;
      case 0x00180015: out.bodyPartExamined         = readString(view, t); break;
      case 0x0020000D: out.studyInstanceUID         = readString(view, t); break;
      case 0x0020000E: out.seriesInstanceUID        = readString(view, t); break;
      case 0x00200013: out.instanceNumber           = parseInt(readString(view, t), 10) || 0; break;
      case 0x00280002: out.samplesPerPixel          = readUS(view, t); break;
      case 0x00280004: out.photometricInterpretation = readString(view, t); break;
      case 0x00280010: out.rows                     = readUS(view, t); break;
      case 0x00280011: out.cols                     = readUS(view, t); break;
      case 0x00280030: out.pixelSpacing             = readString(view, t); break;
      case 0x00280100: out.bitsAllocated            = readUS(view, t); break;
      case 0x00280101: out.bitsStored               = readUS(view, t); break;
      case 0x00280102: out.highBit                  = readUS(view, t); break;
      case 0x00280103: out.pixelRepresentation      = readUS(view, t); break;
      case 0x00281050: out.windowCenter             = readDS(view, t); break;
      case 0x00281051: out.windowWidth              = readDS(view, t); break;
      case 0x00281052: out.rescaleIntercept         = readDS(view, t); break;
      case 0x00281053: out.rescaleSlope             = readDS(view, t); break;
      case 0x7FE00010:
        if (t.undefinedLength) throw new Error('dicom-parser: encapsulated PixelData not supported');
        out.pixelDataOffset = t.valueOffset;
        out.pixelDataLength = t.length;
        out.pixelData = new Uint8Array(arrayBuffer, t.valueOffset, t.length);
        return out;
    }
    off = t.nextOffset;
  }
  throw new Error('dicom-parser: PixelData not found');
}

// Convenience for the worklist row metadata.
export function summary(parsed) {
  return {
    studyInstanceUID: parsed.studyInstanceUID,
    modality: parsed.modality,
    bodyPart: parsed.bodyPartExamined,
    rows: parsed.rows,
    cols: parsed.cols,
    bitsAllocated: parsed.bitsAllocated,
    windowCenter: parsed.windowCenter,
    windowWidth: parsed.windowWidth,
  };
}
